import React, { useMemo } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";
import EmptyValue from "../../../components/EmptyValue";
import TableSkeleton from "../../../components/TableSkeleton";
import ChipSingleSelect from "../../../components/filters/ChipSingleSelect";
import SearchInput from "../../../components/filters/SearchInput";
import Pagination from "../../../components/Pagination";
import useProcurementTable from "./useProcurementTable";
import ReceiveModal from "./ReceiveModal";
import BatchStockModal from "./BatchStockModal";
import ProcurementCart from "./cart/ProcurementCart";
import {
    ProcurementTabs,
    FILTER_CHIPS,
    PARAM_FILTER,
    PARAM_PAGE,
    ShortagesOnlyToggle,
    LookaheadSelect,
    PageSizeSelect,
    getViewDemand,
    StatusBadge,
    RowAction,
    ViewBatchesButton,
    LedgerLink,
    OrderIdPills,
    PartialViewNotice,
    SuggestedQuantity,
    SplitVendorNotice,
    StripSizeInput,
    UnavailableBadge,
    StockHealthNotice,
    StockTotalNotice,
} from "./shared";

// Reorder — prototype-matched (~/Desktop/IventoryDashboard/app.js's ProcurementNeedsTable
// + "Reorder medicines list"), and DELIBERATELY a different table shape from
// Overall/New's shared ProcurementView.jsx, not the same table with fewer columns — the
// prototype itself renders this tab through a structurally different component.
//
// Lapsing-kit demand (A confident + v*B speculative) only, NO buffer: purchase gap is
// max(A + v*B - stock, 0) — Reorder has no committed demand of its own to hold a reorder
// point against, unlike New/Overall which both always carry +S. Same partial-view caveat
// as New — see shared.js's PARTIAL_VIEW_COPY.
//
// The prototype's Actions DROPDOWN (To be ordered / Ordered / Not needed) is REPLACED by
// the Mark Requested button (product-confirmed): its three values mapped to nothing we
// persist — frontend-only state in the prototype — and would disagree with the real
// purchase-order status this screen already derives from inventoryOrderItems.
function ReorderProcurementView() {
    const view = "reorder";
    const {
        isLoading,
        isFetching,
        isError,
        refetch,
        readAt,
        lapsingEnabled,
        isRowUnavailable,
        getSuggestedQuantity,
        viewRows,
        visibleRows,
        chipCounts,
        filterValue,
        searchParams,
        setSearchParams,
        batchesRow,
        setBatchesRow,
        receivingRow,
        setReceivingRow,
        settingStripSizeItemId,
        handleSetStripSize,
        page,
        pageSize,
        total,
        setPage,
        shortOnly,
        setShortOnly,
        lookaheadDays,
        setLookaheadDays,
        countsAreForPage,
        setPageSize,
        searchValue,
        setSearch,
    } = useProcurementTable(view);

    // Prototype-matched summary cards (procurementCronSummary) — KEPT: "Lookahead
    // window" (the dropdown's choice, default our LOOKAHEAD_DAYS = 15) and "Triggered orders" (distinct lapsing userOrder ids across every
    // medicine in this view, deduped, + the medicine-line count after grouping).
    // DROPPED (product-confirmed): "Cron run date" — we have no cron; the prototype's
    // own subtext even called it "Demo preview" — and the green "Daily cron logic..."
    // note, which describes a scheduler we did not build (§Phase-7-UI is a live read on
    // every page load, not a daily batch job).
    const triggeredOrdersCount = useMemo(
        () => new Set(viewRows.flatMap((row) => row.reorderOrderIds || [])).size,
        [viewRows]
    );
    const triggeredMedicinesCount = viewRows.length;

    const columns = [
        {
            key: "medicineName",
            label: "Medicine name",
            render: (row) => <span className="font-semibold">{row.medicineName || <EmptyValue />}</span>,
        },
        {
            key: "orderLink",
            label: "Order ID - Med Needed",
            render: (row) => <OrderIdPills orderIds={row.reorderOrderIds} />,
        },
        {
            key: "tabletsNeededForOrder",
            label: "Total quantity of medicines needed",
            render: (row) => (row.demandUnknown ? <EmptyValue /> : getViewDemand(row, view, lapsingEnabled)),
        },
        {
            key: "tabletsInInventory",
            label: "Total quantity present in inventory",
            render: (row) => (
                <>
                    {isRowUnavailable(row) ? <EmptyValue /> : row.onHand}
                    <ViewBatchesButton row={row} onClick={setBatchesRow} />
                </>
            ),
        },
        { key: "inTransit", label: "In transit", render: (row) => (isRowUnavailable(row) ? <EmptyValue /> : row.inTransit) },
        {
            key: "quantityToBeProcured",
            label: "Quantity of medicines to be procured",
            render: (row) => {
                // §Phase-7-UI (2026-09-09) — see ProcurementView.jsx's identical check
                // for why this comes before isRowUnavailable.
                if (row.stripSize == null) {
                    return (
                        <StripSizeInput
                            row={row}
                            onSet={handleSetStripSize}
                            isSaving={settingStripSizeItemId === row.itemId}
                        />
                    );
                }
                if (isRowUnavailable(row)) return <UnavailableBadge row={row} />;
                // Read-only suggestion (2026-10-27), same as Overall/New — but no "+1 strip
                // buffer" note: Reorder's own formula (A + v*B) carries no buffer at all.
                return <SuggestedQuantity row={row} suggested={getSuggestedQuantity(row)} />;
            },
        },
        { key: "consumptionPattern", label: "Consumption pattern", render: (row) => <LedgerLink row={row} /> },
        { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} /> },
        {
            key: "action",
            label: "Action",
            render: (row) => (
                <RowAction
                    row={row}
                    onReceive={setReceivingRow}
                    cartQuantity={isRowUnavailable(row) ? undefined : getSuggestedQuantity(row) ?? undefined}
                    view={view}
                    lapsingEnabled={lapsingEnabled}
                    lookaheadDays={lookaheadDays}
                />
            ),
        },
    ];

    return (
        <PageContainer>
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-[22px] font-bold text-[var(--ops-carbon-900)]">Procurement</h1>
                    {readAt && (
                        <p className="mt-1 text-xs text-[var(--ops-carbon-500)]">Stock read {moment(readAt).fromNow()}</p>
                    )}
                </div>
                <div className="flex items-center gap-4">
                    <Link
                        to="/v2/inventory/procurement/purchase-orders"
                        className="text-sm font-semibold text-[var(--ops-emerald-600)] hover:underline"
                    >
                        Purchase orders →
                    </Link>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        disabled={isFetching}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[10px] border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] text-[var(--ops-carbon-900)] font-semibold text-[13px] disabled:opacity-70 disabled:cursor-wait enabled:hover:bg-[var(--ops-emerald-50)] enabled:hover:border-[var(--ops-emerald-200)]"
                    >
                        {isFetching ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </header>

            <ProcurementTabs />
            <PartialViewNotice />

            <StockHealthNotice rows={viewRows} />
            <StockTotalNotice rows={viewRows} />

            {lapsingEnabled && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
                        <div className="text-xs font-semibold text-[var(--ops-carbon-500)]">Lookahead window</div>
                        <div className="text-lg font-bold text-[var(--ops-carbon-900)]">
                            {lookaheadDays != null ? `N+${lookaheadDays}` : <EmptyValue />}
                        </div>
                        <div className="mt-1 text-[10px] text-[var(--ops-carbon-500)]">
                            Completed orders already in the reorder bucket or entering it in the next {lookaheadDays ?? "—"} days.
                        </div>
                    </div>
                    <div className="rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
                        <div className="text-xs font-semibold text-[var(--ops-carbon-500)]">Triggered orders</div>
                        <div className="text-lg font-bold text-[var(--ops-carbon-900)]">{triggeredOrdersCount}</div>
                        <div className="mt-1 text-[10px] text-[var(--ops-carbon-500)]">
                            {triggeredMedicinesCount} medicine line item{triggeredMedicinesCount === 1 ? "" : "s"} after grouping by medicine.
                        </div>
                    </div>
                </div>
            )}

            <SplitVendorNotice />

            <section aria-label="Filters" className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)] p-4">
                <ChipSingleSelect
                    label="Filter"
                    inline
                    options={FILTER_CHIPS.map((chip) => ({ id: chip.id, label: `${chip.label} (${chipCounts[chip.id] ?? 0})` }))}
                    value={filterValue}
                    onChange={(nextFilter) => {
                        const next = new URLSearchParams(searchParams);
                        if (nextFilter && nextFilter !== "all") next.set(PARAM_FILTER, nextFilter);
                        else next.delete(PARAM_FILTER);
                        next.delete(PARAM_PAGE);
                        setSearchParams(next, { replace: true });
                    }}
                />
                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex-1 min-w-[220px]">
                        <SearchInput value={searchValue} onChange={setSearch} placeholder="Search a medicine by name" />
                    </div>
                    <LookaheadSelect value={lookaheadDays} onChange={setLookaheadDays} />
                    <ShortagesOnlyToggle checked={shortOnly} onChange={setShortOnly} />
                    {countsAreForPage && (
                        <span className="text-[11px] text-[var(--ops-carbon-500)]">Counts show the shortages on this page only.</span>
                    )}
                </div>
            </section>

            {isLoading ? (
                <div className="mt-4 w-full overflow-x-auto rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <table className="w-full min-w-full border-collapse text-sm">
                        <tbody>
                            <TableSkeleton rows={10} cols={columns.length} />
                        </tbody>
                    </table>
                </div>
            ) : isError ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState title="Couldn't load shortages" hint="Something went wrong reaching the service. Try again in a moment." />
                </div>
            ) : !lapsingEnabled ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState
                        title="This view needs the lapsing-kit prediction turned on"
                        hint="Reorder splits predicted demand from committed demand — enable INVENTORY_PROCUREMENT_LAPSING to see it."
                    />
                </div>
            ) : visibleRows.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-[var(--ops-carbon-200)] bg-[var(--ops-carbon-25)]">
                    <EmptyState title="No shortages" hint="Nothing needs ordering in this view right now." />
                </div>
            ) : (
                <DataTable columns={columns} rows={visibleRows} emptyCell={<EmptyValue />} />
            )}

            {!isLoading && !isError && total > 0 && (
                <div className="mt-4">
                    <div className="flex justify-end mb-2">
                        <PageSizeSelect value={pageSize} onChange={setPageSize} />
                    </div>
                    {total > pageSize && <Pagination page={page} size={pageSize} total={total} onChange={setPage} />}
                </div>
            )}

            <ProcurementCart />
            {batchesRow && <BatchStockModal row={batchesRow} view={view} lapsingEnabled={lapsingEnabled} lookaheadDays={lookaheadDays} onClose={() => setBatchesRow(null)} />}
            {receivingRow && <ReceiveModal row={receivingRow} onClose={() => setReceivingRow(null)} />}
        </PageContainer>
    );
}

export default ReorderProcurementView;
